import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Icon } from '../Icon';
import { ActionMenu, type MenuItem } from '../Dropdown';
import { HorizontalStrip } from '../HorizontalStrip';
import { store } from '../../state/store';
import { useStore } from '../useStore';
import { RENDER_QUALITIES, RENDER_STYLES, RenderTimeout, aiRenderIsTest, buildPayload, deleteRender, deviceId, renderSize, stylePreview, submitRender, waitForRender, type RenderQuality, type RenderType } from '../../state/aiRender';
import { resumeRender, resumeRunning } from '../../state/renderResume';
import { CAMERA_ASPECTS, CAMERA_ZOOMS, FLASH_FADE_MS, FLASH_HOLD_MS, cropForCanvas, viewfinderRect, type CameraAspect, type Rect } from '../../state/cameraMode';
import { gallery, newGalleryId, type GalleryItem } from '../../state/gallery';
import { cultureName, language, t, type TranslationKey } from '../../i18n/I18n';

/** What the camera needs from the 3D view. */
export interface CameraSceneAccess {
  snapshot(maxSide: number, crop?: Rect): Promise<Blob | null>;
  canvas(): HTMLCanvasElement | null;
  setZoom(factor: number): void;
}

type Mode = 'render' | 'redesign';
type Phase = 'idle' | 'shooting' | 'rendering' | 'result' | 'error';
const MODES: Array<{ id: Mode; label: TranslationKey; type: RenderType }> = [
  { id: 'render', label: '#CameraAiPhoto', type: 'renderai' }, { id: 'redesign', label: '#CameraAiRedesign', type: 'airender' },
];
type CapturedFrame = { jpeg: Blob; width: number; height: number; url: string };
/** Unity's BackgroundProgressBar: the bar creeps from 1 to 95 % over the expected time; the result completes it. */
const PROGRESS_EXPECTED_MS: Record<RenderType, number> = { renderai: 45_000, airender: 75_000 };
const SNAPSHOT_MAX_SIDE = 1600;
/** How often unfinished renders are looked up on the server while the camera is open. */
const RESUME_EVERY_MS = 10_000;
const LONG_WAIT_MS = 120_000;
const transportNow = () => ({ device: deviceId(), test: aiRenderIsTest() });

export function formatElapsed(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1000));
  return seconds < 60 ? t('#CameraElapsedSeconds', seconds) : `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

export function useGalleryItems(projectId: string): GalleryItem[] {
  const [, bump] = useState(0);
  useEffect(() => { void gallery.load(); return gallery.subscribe(() => bump((n) => n + 1)); }, []);
  return gallery.list(projectId);
}

/** A picture for a gallery item: a render's server copy, or its source photo while it is still running. */
function pictureOf(item: GalleryItem): string | undefined {
  const own = gallery.thumbUrl(item);
  if (own || item.kind !== 'render' || !item.sourceId) return own;
  const source = gallery.get(item.sourceId);
  return source ? gallery.imageUrl(source) : undefined;
}
function beforeOf(item: GalleryItem): string | undefined {
  const source = item.sourceId ? gallery.get(item.sourceId) : undefined;
  return (source && gallery.imageUrl(source)) ?? item.initImageUrl;
}

/** The app's own render animation, taken from `StreamingAssets/Gif/ai-render.gif`. */
export const RENDER_ANIMATION = '/hp/ui/ai-render.gif';
export function RenderSparkles({ size = 44 }: { size?: number }) {
  return <>
    <img className="render-sparkles" src={RENDER_ANIMATION} width={size} height={size} alt="" aria-hidden="true" />
    <span className="render-sparkles-static" style={{ width: size, height: size }} aria-hidden="true"><Icon name="sparkles" size={Math.round(size * .55)} /></span>
  </>;
}

/**
 * Press and hold to see the photo the render was made from. A redesign changes the room,
 * so the question is "what was here before"; a plain photoreal render answers it by
 * looking the same, and carries no control at all.
 */
function HoldOriginal({ onHold }: { onHold: (held: boolean) => void }) {
  const release = () => onHold(false);
  return (
    <button
      className="camera-hold" aria-label={t('#CameraHoldOriginal')}
      onPointerDown={(event) => { event.currentTarget.setPointerCapture(event.pointerId); onHold(true); }}
      onPointerUp={release} onPointerCancel={release} onLostPointerCapture={release} onBlur={release}
      onKeyDown={(event) => { if (event.key === ' ' || event.key === 'Enter') { event.preventDefault(); onHold(true); } }}
      onKeyUp={(event) => { if (event.key === ' ' || event.key === 'Enter') onHold(false); }}
    ><Icon name="eye" size={18} /></button>
  );
}

/** One filter tile: the style's picture with its name, on a skeleton until the picture arrives. */
function StyleTile({ id, label, selected, disabled, tabbable, onSelect, register }: {
  id: string; label: string; selected: boolean; disabled: boolean; tabbable: boolean;
  onSelect: () => void; register: (el: HTMLButtonElement | null) => void;
}) {
  const [loaded, setLoaded] = useState(false);
  return (
    <button
      ref={register} type="button" role="radio" aria-checked={selected} tabIndex={tabbable ? 0 : -1}
      className="camera-style" disabled={disabled} title={label} onClick={onSelect}
    >
      <span className="camera-style-shot">
        <span className="camera-style-skeleton" hidden={loaded} aria-hidden="true" />
        <img
          src={stylePreview(id)} alt="" loading="lazy" decoding="async" width={72} height={72}
          className={loaded ? 'is-ready' : undefined}
          ref={(image) => { if (image?.complete && image.naturalWidth) setLoaded(true); }}
          onLoad={() => setLoaded(true)} onError={(event) => { event.currentTarget.removeAttribute('src'); setLoaded(true); }}
        />
      </span>
      <span className="camera-style-name">{label}</span>
    </button>
  );
}

/**
 * The redesign styles as a filter strip, the way a photo editor shows filters: every style
 * is on screen with its own picture, one tap away, instead of behind a menu. The pictures
 * are the app's own gallery previews (`tools/stage-style-previews.py`).
 *
 * A radio group, so the keyboard walks it with the arrows and the strip follows the
 * selection; the pictures load lazily, and a tile answers the tap before its picture lands.
 */
function StyleFilters({ value, disabled, onChange }: { value: string; disabled: boolean; onChange: (id: string) => void }) {
  const strip = useRef<HTMLDivElement | null>(null);
  const tiles = useRef(new Map<string, HTMLButtonElement>());
  /** Keep the chosen style on screen — when the strip opens, and when the keyboard moves it. */
  const reveal = (id: string, smooth: boolean) => {
    const box = strip.current, tile = tiles.current.get(id);
    if (!box || !tile) return;
    const left = tile.offsetLeft - (box.clientWidth - tile.offsetWidth) / 2;
    box.scrollTo({ left: Math.max(0, left), behavior: smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto' });
  };
  useLayoutEffect(() => { reveal(value, false); }, []);

  const step = (by: number) => {
    const index = RENDER_STYLES.findIndex((s) => s.id === value);
    const next = RENDER_STYLES[Math.min(RENDER_STYLES.length - 1, Math.max(0, index + by))];
    if (!next || next.id === value) return;
    onChange(next.id);
    reveal(next.id, true);
    tiles.current.get(next.id)?.focus();
  };
  const keys: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
  const onKeyDown = (event: ReactKeyboardEvent) => {
    if (disabled) return;
    if (event.key in keys) { event.preventDefault(); step(keys[event.key]); return; }
    if (event.key !== 'Home' && event.key !== 'End') return;
    event.preventDefault();
    step(event.key === 'Home' ? -RENDER_STYLES.length : RENDER_STYLES.length);
  };

  return (
    <HorizontalStrip scrollerRef={strip} className="camera-styles" shellClassName="camera-styles-shell" role="radiogroup" aria-label={t('#CameraRedesignStyle')} onKeyDown={onKeyDown}>
      {RENDER_STYLES.map((item) => (
        <StyleTile
          key={item.id} id={item.id} label={item.label} selected={item.id === value} disabled={disabled}
          tabbable={item.id === value}
          register={(el) => { if (el) tiles.current.set(item.id, el); else tiles.current.delete(item.id); }}
          onSelect={() => { onChange(item.id); reveal(item.id, true); }}
        />
      ))}
    </HorizontalStrip>
  );
}

/**
 * A viewfinder for Аи фото / Аи редизайн. The input frame stays in memory for preview and retry;
 * only the render is stored in the gallery, updating in place when its result arrives.
 */
export function CameraMode({ scene }: { scene: CameraSceneAccess }) {
  const state = useStore((s) => ({ projectId: s.projectId, projectName: s.projectName, roomType: s.scene?.roomType }));
  const root = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [aspect, setAspect] = useState<CameraAspect>('auto');
  const [zoom, setZoom] = useState(1);
  const [mode, setModeState] = useState<Mode>(() => { try { const saved = localStorage.getItem('hp.cameraMode'); return MODES.some((m) => m.id === saved) ? (saved as Mode) : 'render'; } catch { return 'render'; } });
  const setMode = (next: Mode) => { setModeState(next); try { localStorage.setItem('hp.cameraMode', next); } catch { /* private mode */ } };
  const [style, setStyleState] = useState(() => { try { const saved = localStorage.getItem('hp.renderStyle'); return RENDER_STYLES.some((s) => s.id === saved) ? saved! : RENDER_STYLES[0].id; } catch { return RENDER_STYLES[0].id; } });
  const setStyle = (next: string) => { setStyleState(next); try { localStorage.setItem('hp.renderStyle', next); } catch { /* private mode */ } };
  const [quality, setQualityState] = useState<RenderQuality>(() => { try { const saved = localStorage.getItem('hp.renderQuality'); return RENDER_QUALITIES.some((q) => q.id === saved && !q.needsAccount) ? (saved as RenderQuality) : 'FullHD'; } catch { return 'FullHD'; } });
  const setQuality = (next: RenderQuality) => { setQualityState(next); try { localStorage.setItem('hp.renderQuality', next); } catch { /* private mode */ } };
  const [prompt, setPrompt] = useState('');
  const [promptOpen, setPromptOpen] = useState(false);
  const [phase, setPhase] = useState<Phase>('idle');
  const [flashKey, setFlashKey] = useState(0);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');
  const [current, setCurrent] = useState<GalleryItem | null>(null);
  const mounted = useRef(true);
  const captured = useRef<CapturedFrame | null>(null);
  const clearCapture = () => { if (captured.current) URL.revokeObjectURL(captured.current.url); captured.current = null; };
  const [startedAt, setStartedAt] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [holding, setHolding] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const items = useGalleryItems(state.projectId);
  /** The render whose finish the frame is waiting to show; cleared when the user moves on. */
  const watching = useRef<string | null>(null);
  /** Renders this camera is awaiting itself; the periodic look-up leaves them alone. */
  const inFlight = useRef(new Set<string>());
  const phaseRef = useRef(phase); phaseRef.current = phase;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; watching.current = null; clearCapture(); }; }, []);

  // Renders the browser stopped waiting for (a closed tab, a wait that ran out) finish here.
  useEffect(() => {
    let stopped = false;
    const look = async () => {
      if (stopped) return;
      const waiting = gallery.list(state.projectId).filter((item) => item.kind === 'render' && item.status === 'running' && !inFlight.current.has(item.id));
      if (waiting.length) await resumeRunning(waiting, (id, patch) => gallery.update(id, patch), { transport: transportNow() });
    };
    void gallery.load().then(look);
    const timer = setInterval(() => { void look(); }, RESUME_EVERY_MS);
    return () => { stopped = true; clearInterval(timer); };
  }, [state.projectId]);

  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const update = () => setSize({ width: el.clientWidth, height: el.clientHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  useEffect(() => { scene.setZoom(zoom); }, [scene, zoom]);
  useEffect(() => () => scene.setZoom(1), [scene]);
  useEffect(() => {
    if (phase !== 'rendering') return;
    const timer = setInterval(() => setElapsed(Date.now() - startedAt), 200);
    return () => clearInterval(timer);
  }, [phase, startedAt]);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(''), 2800);
    return () => clearTimeout(timer);
  }, [toast]);

  const rect = viewfinderRect(size, aspect);
  const busy = phase === 'shooting' || phase === 'rendering';
  const type = MODES.find((m) => m.id === mode)?.type ?? 'renderai';

  const leave = () => { watching.current = null; clearCapture(); setCurrent(null); setError(''); setHolding(false); setPhase('idle'); };

  const render = async (frame: CapturedFrame, retryId?: string) => {
    const renderType: RenderType = type;
    const id = retryId ?? newGalleryId('render');
    watching.current = id; inFlight.current.add(id);
    phaseRef.current = 'rendering'; setPhase('rendering');
    const item = await gallery.add({
      id, projectId: state.projectId, kind: 'render', createdAt: Date.now(), status: 'running', type: renderType,
      style: renderType === 'airender' ? style : undefined, prompt: prompt.trim() || undefined, width: frame.width, height: frame.height,
    });
    if (watching.current === id) { setCurrent(item); setError(''); setStartedAt(Date.now()); setElapsed(0); }
    try {
      const payload = buildPayload({ type: renderType, prompt, style, roomType: state.roomType });
      const transport = transportNow();
      const size = renderSize(quality, frame);
      const { id: renderId } = await submitRender({ type: renderType, payload, jpeg: frame.jpeg, title: state.projectName || 'Home Planner Web', locale: language(), size }, transport);
      console.info('[ai-render] sent', renderType, renderId);
      await gallery.update(id, { renderId });
      const result = await waitForRender(renderId, transport);
      console.info('[ai-render] ready', renderId, result.imageUrl);
      const done = await gallery.update(id, {
        status: 'done', imageUrl: result.imageUrl, thumbUrl: result.thumbUrl, initImageUrl: result.initImageUrl,
        width: result.width ?? frame.width, height: result.height ?? frame.height,
      });
      if (watching.current === id && done) { setCurrent(done); setHolding(false); setPhase('result'); }
    } catch (e) {
      if (e instanceof RenderTimeout) {
        // Still rendering on the server: the gallery keeps its spinner and the look-up finishes it.
        console.warn('[ai-render] waiting longer than usual', e.renderId);
        if (watching.current === id) { leave(); setToast(t('#CameraStillRenderingToast')); }
        return;
      }
      const message = e instanceof Error ? e.message : String(e);
      console.warn('[ai-render] error', message);
      const failed = await gallery.update(id, { status: 'error', error: message });
      if (watching.current === id) { if (failed) setCurrent(failed); setError(message); setPhase('error'); }
    } finally {
      inFlight.current.delete(id);
    }
  };

  const shoot = async () => {
    if (phaseRef.current === 'shooting' || phaseRef.current === 'rendering') return;
    const el = root.current, canvas = scene.canvas();
    if (!el || !canvas) { setError(t('#CameraSceneNotReady')); setPhase('error'); return; }
    phaseRef.current = 'shooting'; setError(''); setPhase('shooting');
    try {
      const canvasBox = canvas.getBoundingClientRect(), box = el.getBoundingClientRect();
      const crop = cropForCanvas({ x: rect.x + box.left - canvasBox.left, y: rect.y + box.top - canvasBox.top, width: rect.width, height: rect.height }, canvas);
      const jpeg = await scene.snapshot(SNAPSHOT_MAX_SIDE, crop);
      if (!mounted.current) return;
      setFlashKey((k) => k + 1);
      if (!jpeg) { setError(t('#CameraShotFailed')); setPhase('error'); return; }
      const scale = Math.min(1, SNAPSHOT_MAX_SIDE / Math.max(crop.width, crop.height));
      clearCapture();
      const frame = { jpeg, width: Math.round(crop.width * scale), height: Math.round(crop.height * scale), url: URL.createObjectURL(jpeg) };
      captured.current = frame;
      console.info('[ai-render] shot', frame.width, frame.height, jpeg.size);
      await render(frame);
    } catch (e) {
      // A shutter that silently sticks is worse than a message: say what broke and let the user try again.
      const message = e instanceof Error ? e.message : String(e);
      console.error('[ai-render] shot failed', e);
      setError(t('#CameraShotFailedWith', message)); setPhase('error');
    }
  };
  const shootRef = useRef(shoot); shootRef.current = shoot;

  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT' || target.tagName === 'SELECT')) return;
      if ((event.target as Element | null)?.closest('.dropdown, button, a')) return;
      if (event.code === 'Space' && !galleryOpen && phaseRef.current === 'idle') { void shootRef.current(); event.preventDefault(); }
    };
    window.addEventListener('keydown', key, true);
    return () => window.removeEventListener('keydown', key, true);
  }, [galleryOpen]);

  const bars = aspect === 'auto' ? [] : [
    { key: 'top', style: { left: 0, top: 0, width: size.width, height: rect.y } },
    { key: 'bottom', style: { left: 0, top: rect.y + rect.height, width: size.width, height: Math.max(0, size.height - rect.y - rect.height) } },
    { key: 'left', style: { left: 0, top: rect.y, width: rect.x, height: rect.height } },
    { key: 'right', style: { left: rect.x + rect.width, top: rect.y, width: Math.max(0, size.width - rect.x - rect.width), height: rect.height } },
  ];
  const frameStyle: CSSProperties = { left: rect.x, top: rect.y, width: rect.width, height: rect.height };
  const last = items[0];
  const progress = phase === 'rendering' ? 1 + 94 * Math.min(1, elapsed / PROGRESS_EXPECTED_MS[type]) : 100;
  const before = current ? captured.current?.url ?? beforeOf(current) : undefined;
  const showShot = current !== null && (phase === 'rendering' || phase === 'result' || phase === 'error');
  const retry = () => {
    if (phaseRef.current === 'rendering' || phaseRef.current === 'shooting') return;
    if (captured.current && current) void render(captured.current, current.id); else leave();
  };

  return (
    <div ref={root} className="camera" data-phase={phase} aria-label={t('Camera')}>
      {bars.map((bar) => <div key={bar.key} className="camera-bar" style={bar.style} />)}
      <div className="camera-frame" style={frameStyle} aria-hidden="true"><i /><i /><i /><i /></div>
      {showShot && current && (
        <div className="camera-shot" style={frameStyle}>
          {phase === 'result' && current.imageUrl ? (
            <>
              <img className="camera-result-image" src={holding && before ? before : current.imageUrl} alt={holding ? t('#CameraOriginalShot') : t('#CameraAiRenderResult')} />
              {current.type === 'airender' && before && <HoldOriginal onHold={setHolding} />}
            </>
          ) : (
            <>
              {before && <img className="camera-shot-photo" src={before} alt={t('#CameraCapturedShot')} />}
              {phase === 'rendering' && <>
                <div className="camera-shimmer" aria-hidden="true" />
                <div className="camera-progress" role="progressbar" aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100}><span style={{ width: `${progress}%` }} /></div>
                <div className="camera-working"><RenderSparkles size={92} />
                  <div className="camera-pill" role="status">{t(elapsed > LONG_WAIT_MS ? '#CameraRenderingElapsedBusy' : '#CameraRenderingElapsed', formatElapsed(elapsed))}</div>
                </div>
              </>}
              {phase === 'error' && <div className="camera-pill error" role="alert">{error}</div>}
            </>
          )}
        </div>
      )}
      {flashKey > 0 && <div key={flashKey} className="camera-flash" style={{ animationDuration: `${FLASH_HOLD_MS + FLASH_FADE_MS}ms` }} aria-hidden="true" />}
      <div className="camera-top">
        <button className="camera-round" aria-label={t('#CameraClose')} onClick={() => store.set({ cameraMode: false })}><Icon name="close" size={20} /></button>
        <div className="camera-chips" role="radiogroup" aria-label={t('#CameraAspect')}>
          {CAMERA_ASPECTS.map((a) => <button key={a.id} role="radio" aria-checked={aspect === a.id} className="camera-chip" disabled={busy} onClick={() => setAspect(a.id)}>{a.label}</button>)}
        </div>
        <div className="camera-top-right">
          {type && <div className="camera-chips camera-quality" role="radiogroup" aria-label={t('#Quality')}>
            {RENDER_QUALITIES.map((q) => <button key={q.id} role="radio" aria-checked={quality === q.id} data-pro={q.needsAccount} className="camera-chip small"
              title={q.needsAccount
                ? t('#CameraQualityNeedsAccount', q.label)
                : t('#CameraQualitySide', q.label, q.side)}
              disabled={busy || q.needsAccount} onClick={() => setQuality(q.id)}>{q.label}</button>)}
          </div>}
          {type && <button className="camera-round" aria-label={t('#CameraPrompt')} aria-pressed={promptOpen} disabled={busy} onClick={() => setPromptOpen(!promptOpen)}><Icon name="pencil" size={18} /></button>}
        </div>
      </div>
      {promptOpen && type && <div className="camera-prompt">
        <textarea value={prompt} maxLength={500} aria-label={t('#CameraPrompt')} placeholder={mode === 'redesign' ? t('#CameraPromptRedesignPlaceholder') : t('#CameraPromptPhotoPlaceholder')} onChange={(event) => setPrompt(event.target.value)} />
        <button className="camera-chip on" onClick={() => setPromptOpen(false)}>{t('Done')}</button>
      </div>}
      {toast && <div className="camera-toast" role="status">{toast}</div>}
      <div className="camera-bottom">
        {phase === 'rendering' ? (
          <div className="camera-result-actions"><button className="camera-chip" onClick={leave}>{t('#CameraMinimize')}</button></div>
        ) : phase === 'result' || (phase === 'error' && current) ? (
          <div className="camera-result-actions">
            {phase === 'result' && current?.imageUrl && <a className="camera-chip" href={current.imageUrl} target="_blank" rel="noopener noreferrer">{t('Open')}</a>}
            {phase === 'error' && <button className="camera-chip" onClick={retry}>{t('#FailedRenderButtonText')}</button>}
            <button className="camera-chip on" onClick={leave}>{t('#CameraAnotherShot')}</button>
            <button className="camera-chip" onClick={() => setGalleryOpen(true)}>{t('Gallery')}</button>
          </div>
        ) : (
          <>
            {error && <div className="camera-pill error" role="alert">{error}</div>}
            {mode === 'redesign' && <StyleFilters value={style} disabled={busy} onChange={setStyle} />}
            <div className="camera-modes" role="tablist" aria-label={t('#CameraModeGroup')}>
              {MODES.map((m) => <button key={m.id} role="tab" aria-selected={mode === m.id} disabled={busy} onClick={() => { setMode(m.id); setError(''); }}>{t(m.label)}</button>)}
            </div>
            <div className="camera-shutter-row">
              <button className="camera-gallery-shortcut" aria-label={t('Gallery')} onClick={() => setGalleryOpen(true)}>
                <span className="camera-thumb">
                {last && pictureOf(last) ? <img src={pictureOf(last)} alt="" /> : <Icon name="gallery" size={22} />}
                {last?.kind === 'render' && <span className="camera-badge"><Icon name="sparkles" size={10} /></span>}
                {last?.status === 'running' && <span className="camera-spinner small" aria-hidden="true" />}
                </span>
                <span className="camera-thumb-label">{t('Gallery')}<span>{t('#CameraShotCount', items.length)}</span></span>
              </button>
              <button className="camera-shutter" aria-label={t('#CameraShoot')} disabled={busy} onClick={() => void shoot()}><span key={flashKey} className={flashKey ? 'camera-shutter-pulse' : undefined} /></button>
              <div className="camera-chips camera-zoom" role="radiogroup" aria-label={t('#CameraZoom')}>
                {CAMERA_ZOOMS.map((z) => <button key={z.value} role="radio" aria-checked={zoom === z.value} className="camera-chip small" onClick={() => setZoom(z.value)}>{z.label}</button>)}
              </div>
            </div>
          </>
        )}
      </div>
      {galleryOpen && <GalleryModal items={items} onClose={() => setGalleryOpen(false)} />}
    </div>
  );
}

/** Lite's gallery: a 3-column grid of photos and renders, a full view with before/after, download and delete. */
/** Today and yesterday read better than a date; older shots keep theirs. */
function dayLabel(at: number, now = Date.now()): string {
  const day = (ms: number) => { const d = new Date(ms); return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime(); };
  const difference = (day(now) - day(at)) / 86_400_000;
  if (difference === 0) return t('#Today');
  if (difference === 1) return t('#Yesterday');
  return new Date(at).toLocaleDateString(cultureName(), { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Newest first, split into the days they were taken. */
export function galleryDays(items: GalleryItem[], now = Date.now()): Array<{ label: string; items: GalleryItem[] }> {
  const days: Array<{ label: string; items: GalleryItem[] }> = [];
  for (const item of items) {
    const label = dayLabel(item.createdAt, now);
    const last = days[days.length - 1];
    if (last?.label === label) last.items.push(item); else days.push({ label, items: [item] });
  }
  return days;
}

const kindLabel = (item: GalleryItem): string =>
  item.kind === 'photo' ? t('#Photo') : item.type === 'airender' ? t('#CameraAiRedesign') : t('#CameraAiPhoto');

/** When it was taken, what was asked and the server's number — on hover, not as a caption. */
function details(item: GalleryItem): string {
  const parts = [new Date(item.createdAt).toLocaleString(cultureName(), { dateStyle: 'short', timeStyle: 'short' })];
  if (item.type === 'airender' && item.style) parts.push(RENDER_STYLES.find((s) => s.id === item.style)?.label ?? item.style);
  if (item.prompt) parts.push(t('#CameraPromptQuoted', item.prompt));
  if (item.renderId) parts.push(t('#CameraRenderNumber', item.renderId.slice(-6)));
  return parts.join(' · ');
}

/**
 * One picture, filling the screen: the gallery is for choosing, this is for looking. Arrows
 * walk the same list, the actions are icons, and a redesign keeps the press-and-hold control.
 */
export function Lightbox({ items, id, onSelect, onClose, readOnly = false }: { items: GalleryItem[]; id: string; onSelect: (id: string) => void; onClose: () => void; readOnly?: boolean }) {
  const [holding, setHolding] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState('');
  const [closing, setClosing] = useState(false);
  const closingRef = useRef(false);
  const onCloseRef = useRef(onClose); onCloseRef.current = onClose;
  const close = () => {
    if (closingRef.current) return;
    closingRef.current = true;
    if (!item || window.matchMedia('(prefers-reduced-motion: reduce)').matches) onCloseRef.current();
    else setClosing(true);
  };
  useEffect(() => {
    if (!closing) return;
    const timer = setTimeout(() => onCloseRef.current(), 140);
    return () => clearTimeout(timer);
  }, [closing]);
  const index = items.findIndex((item) => item.id === id);
  const item = items[index];
  const step = (by: number) => { const next = items[index + by]; if (next) { onSelect(next.id); setHolding(false); setConfirming(false); setNote(''); } };
  const stepRef = useRef(step); stepRef.current = step;

  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
      else if (event.key === 'ArrowLeft') { if (!closingRef.current) stepRef.current(-1); }
      else if (event.key === 'ArrowRight') { if (!closingRef.current) stepRef.current(1); }
      else return;
      event.stopImmediatePropagation();
      event.preventDefault();
    };
    window.addEventListener('keydown', key, true);
    return () => window.removeEventListener('keydown', key, true);
  }, []);
  useEffect(() => {
    if (!confirming) return;
    const timer = setTimeout(() => setConfirming(false), 4000);
    return () => clearTimeout(timer);
  }, [confirming]);
  useLayoutEffect(() => { if (!item) close(); }, [item]);
  if (!item) return null;

  const picture = item.kind === 'render' ? (item.imageUrl ?? pictureOf(item)) : gallery.imageUrl(item);
  const before = item.kind === 'render' && item.status === 'done' ? beforeOf(item) : undefined;
  const look = async () => {
    setNote(t('#CameraAskingServer'));
    const patch = await resumeRender(item, { transport: transportNow() });
    if (patch) await gallery.update(item.id, patch);
    setNote(patch ? '' : t('#CameraServerStillRendering'));
  };

  return (
    <div className="camera-lightbox" data-closing={closing} role="dialog" aria-modal="true" aria-label={kindLabel(item)}
      onClickCapture={(event) => { if (closingRef.current) { event.preventDefault(); event.stopPropagation(); } }} onClick={(event) => { event.stopPropagation(); close(); }}>
      <header className="camera-lightbox-head" inert={closing} onClick={(event) => event.stopPropagation()}>
        <h2 title={details(item)}>{kindLabel(item)}</h2>
        <span className="camera-lightbox-count">{t('#CameraLightboxCount', index + 1, items.length)}</span>
        <button className="gallery-act on-dark" title={t('Close')} aria-label={t('#CameraCloseViewer')} onClick={close}><Icon name="close" size={20} /></button>
      </header>
      <div className="camera-lightbox-stage" inert={closing} onClick={(event) => event.stopPropagation()}>
        {index > 0 && <button className="camera-lightbox-step" data-side="left" title={t('#CameraPrevious')} aria-label={t('#CameraPreviousShot')} onClick={() => step(-1)}><Icon name="back" size={22} /></button>}
        <div key={item.id} className="camera-gallery-image">
          {picture && <img src={holding && before ? before : picture} alt={holding ? t('#CameraOriginalShot') : kindLabel(item)} style={item.status === 'running' ? { filter: 'brightness(.5)' } : undefined} />}
          {before && item.type === 'airender' && <HoldOriginal onHold={setHolding} />}
          {item.status === 'running' && <div className="camera-working"><RenderSparkles size={112} /><div className="camera-pill" role="status">{note || t('#CameraRendering')}</div></div>}
          {item.status === 'error' && <div className="camera-pill error" role="alert">{item.error ?? t('#CameraRenderFailed')}</div>}
        </div>
        {index < items.length - 1 && <button className="camera-lightbox-step" data-side="right" title={t('#CameraNext')} aria-label={t('#CameraNextShot')} onClick={() => step(1)}><Icon name="next" size={22} /></button>}
      </div>
      <div className="camera-gallery-actions on-dark" inert={closing} onClick={(event) => event.stopPropagation()}>
        {item.kind === 'render' && item.status === 'running' &&
          <button className="gallery-act on-dark" title={t('#CameraAskServer')} aria-label={t('#CameraAskServer')} onClick={() => void look()}><Icon name="recent" size={19} /></button>}
        {item.kind === 'render' && item.imageUrl &&
          <a className="gallery-act on-dark" title={t('#CameraOpenOriginal')} aria-label={t('#CameraOpenOriginal')} href={item.imageUrl} target="_blank" rel="noopener noreferrer"><Icon name="external" size={19} /></a>}
        {item.kind === 'photo' && picture &&
          <a className="gallery-act on-dark" title={t('Download')} aria-label={t('Download')} href={picture} download={`home-planner-${item.createdAt}.jpg`}><Icon name="download" size={19} /></a>}
        {confirming && <span className="camera-delete-note" role="status">{t('#CameraDeleteAgainNote')}</span>}
        {!readOnly && <button className="gallery-act on-dark danger" data-confirm={confirming} title={confirming ? t('#CameraDeleteConfirm') : t('Delete')}
          aria-label={confirming ? t('#CameraDeleteConfirm') : t('Delete')}
          onClick={() => { if (confirming) void gallery.remove(item.id); else setConfirming(true); }}><Icon name="trash" size={19} /></button>}
      </div>
    </div>
  );
}

/**
 * The project's gallery as a floating panel: the days it was shot, a grid of photos,
 * and everything a card can do behind its own menu. A render still on the server keeps its
 * source photo under the app's own animation, with the way out — "Отмена" drops the draft
 * on the server too. A card opens full screen.
 */
/** `readOnly`: a guest's look — no new frame, no delete, no cancel of a render. */
export function GalleryModal({ items, onClose, onNewFrame, readOnly = false }: { items: GalleryItem[]; onClose: () => void; onNewFrame?: () => void; readOnly?: boolean }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [askDelete, setAskDelete] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);
  const closingRef = useRef(false);
  const onCloseRef = useRef(onClose); onCloseRef.current = onClose;
  const completion = useRef<(() => void) | null>(null);
  const finishClose = () => { onCloseRef.current(); completion.current?.(); };
  const close = () => {
    if (closingRef.current) return;
    closingRef.current = true;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) finishClose();
    else setClosing(true);
  };
  useEffect(() => {
    if (!closing) return;
    const timer = setTimeout(finishClose, 160);
    return () => clearTimeout(timer);
  }, [closing]);
  useEffect(() => {
    if (openId) return;
    const key = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || document.querySelector('[data-dropdown-open=true]')) return;
      close(); event.stopImmediatePropagation(); event.preventDefault();
    };
    window.addEventListener('keydown', key, true);
    return () => window.removeEventListener('keydown', key, true);
  }, [openId]);
  /** Stop waiting and drop the draft the server is holding (contract §7). */
  const cancel = async (item: GalleryItem) => {
    if (item.renderId) await deleteRender(item.renderId, transportNow()).catch((error) => console.warn('[ai-render] cancel', error));
    await gallery.remove(item.id);
  };
  const show = (id: string) => { setOpenId(id); };
  const stamp = (item: GalleryItem) => new Date(item.createdAt).toLocaleString(cultureName(), { dateStyle: 'short', timeStyle: 'short' });
  const fileOf = (item: GalleryItem) => (item.kind === 'render' ? item.imageUrl : gallery.imageUrl(item));

  return (
    <div className="modal-backdrop camera-gallery-backdrop" data-closing={closing}
      onClickCapture={(event) => { if (closingRef.current) { event.preventDefault(); event.stopPropagation(); } }} onClick={close}>
      <section className="camera-gallery" inert={closing} data-behind={openId !== null} role="dialog" aria-modal="true" aria-label={t('Gallery')} onClick={(event) => event.stopPropagation()}>
        <header className="camera-gallery-head">
          <div><h2>{t('Gallery')}</h2><p>{items.length === 0 ? t('#CameraGalleryEmptySubtitle') : t('#CameraShotCount', items.length)}</p></div>
          <button className="round-btn" aria-label={t('#CameraCloseGallery')} onClick={close}><Icon name="close" size={19} /></button>
        </header>
        {items.length === 0 ? (
          <div className="camera-gallery-empty"><span><Icon name="gallery" size={32} /></span><h3>{t('#CameraGalleryEmptyTitle')}</h3><p>{t('#CameraGalleryEmptyText')}<br />{t('#CameraGalleryEmptyHint')}</p></div>
        ) : (
          <div className="camera-gallery-body">
            {galleryDays(items).map((day) => (
              <section key={day.label} className="camera-gallery-day">
                <h3>{day.label}</h3>
                <div className="camera-gallery-grid">
                  {day.items.map((item) => {
                    const thumb = pictureOf(item);
                    return (
                      <figure key={item.id} className="camera-gallery-item" data-kind={item.kind} data-status={item.status ?? 'done'}>
                        <button className="camera-gallery-cell" data-kind={item.kind} data-status={item.status ?? 'done'}
                          aria-label={`${kindLabel(item)} ${stamp(item)}`} onClick={() => show(item.id)}>
                          {thumb ? <img src={thumb} alt="" loading="lazy" /> : <span className="camera-gallery-blank" />}
                        </button>
                        <span className="camera-gallery-tag">{item.kind === 'render' && <Icon name="sparkles" size={11} />}{kindLabel(item)}</span>
                        <ActionMenu className="camera-gallery-dropdown" label={t('#CameraItemActions', kindLabel(item), stamp(item))} trigger={<Icon name="more" size={18} />} triggerClassName="camera-gallery-more" contain=".camera-gallery"
                          onOpenChange={() => setAskDelete(null)} heading={askDelete === item.id ? t('#CameraDeleteForever') : undefined}
                          items={askDelete === item.id ? [
                            { id: 'confirm-delete', label: t('Delete'), icon: <Icon name="trash" size={17} />, danger: true, onSelect: () => { void gallery.remove(item.id); } },
                            { id: 'cancel-delete', label: t('Cancel'), keepOpen: true, onSelect: () => setAskDelete(null) },
                          ] : [
                            { id: 'open', label: t('Open'), icon: <Icon name="search" size={17} />, onSelect: () => show(item.id) },
                            ...(fileOf(item) ? [{ id: 'file', label: item.kind === 'render' ? t('#CameraOpenOriginal') : t('Download'), icon: <Icon name={item.kind === 'render' ? 'external' : 'download'} size={17} />, href: fileOf(item), target: item.kind === 'render' ? '_blank' : undefined, download: item.kind === 'photo' ? `home-planner-${item.createdAt}.jpg` : undefined }] : []),
                            ...(readOnly ? [] : [{ id: 'separator', separator: true } as const,
                              { id: 'delete', label: t('Delete'), icon: <Icon name="trash" size={17} />, danger: true, keepOpen: true, onSelect: () => setAskDelete(item.id) }]),
                          ] satisfies MenuItem[]} />
                        {item.status === 'running' && (
                          <div className="camera-gallery-working">
                            <RenderSparkles size={54} />
                            <span>{t('#CameraRenderingShort')}</span>
                            {!readOnly && <button onClick={(event) => { event.stopPropagation(); void cancel(item); }}>{t('Cancel')}</button>}
                          </div>
                        )}
                        {item.status === 'error' && <span className="camera-cell-error">{t('Error')}</span>}
                      </figure>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
        {!readOnly && <footer className="camera-gallery-foot">
          <button className="camera-gallery-new" onClick={() => { if (closingRef.current) return; completion.current = onNewFrame ?? null; close(); }}><Icon name="camera" size={20} />{t('#CameraNewShot')}</button>
        </footer>}
      </section>
      {openId && <Lightbox items={items} id={openId} onSelect={setOpenId} onClose={() => setOpenId(null)} readOnly={readOnly} />}
    </div>
  );
}
